import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from './Icon';
import VariantOptionLabel from './VariantOptionLabel';
import clsx from 'clsx';

export interface DropdownOption {
  value: string;
  label?: string;
  stockStatusDescription?: string;
  inStock: boolean;
  stockstatus?: number;
  articlenumber: string;
  disabled?: boolean;
}

interface DropdownAttributes {
  options: DropdownOption[];
  onChange?: (option: DropdownOption) => void;
  selectedValue?: string;
  className?: string;
  size?: 'small' | 'large';
  placeholder?: string;
  disabled?: boolean;
  isVariantDropdown?: boolean;
}

const Dropdown = ({
  options,
  selectedValue: initialSelectedValue,
  onChange,
  className = '',
  size = 'large',
  placeholder,
  disabled = false,
  isVariantDropdown = false,
}: DropdownAttributes) => {
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    initialSelectedValue ?? placeholder
  );
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const selectedOption = useMemo(
    () => options?.find((o) => o.value == selectedValue),
    [selectedValue, options]
  );
  const onKeyPress = (e) => {
    const enterOrSpace =
      e.key === 'Enter' ||
      e.key === ' ' ||
      e.key === 'Spacebar' ||
      e.which === 13 ||
      e.which === 32;
    if (enterOrSpace) {
      e.preventDefault();
      if (!dropdownOpen) {
        setDropdownOpen(!dropdownOpen);
      }
    }
  };
  const onKeyPressOption = (e, selectedOption) => {
    const enterOrSpace =
      e.key === 'Enter' ||
      e.key === ' ' ||
      e.key === 'Spacebar' ||
      e.which === 13 ||
      e.which === 32;
    if (enterOrSpace) {
      e.preventDefault();
      onOptionSelected(selectedOption);
      setDropdownOpen(false);
    }
  };
  useEffect(() => {
    let _selectedValue = options?.find(
      (o) =>
        o.value == selectedValue &&
        (o.disabled == false || o.disabled == undefined)
    );
    if (!_selectedValue) {
      setSelectedValue(undefined);
    }
  }, [options]);

  const onOptionSelected = (value: string | undefined) => {
    setSelectedValue(value);
    const selectedOption = options?.find((o) => o.value == value);
    if (onChange) {
      onChange(selectedOption);
    }
    setDropdownOpen(false);
  };
  const handleOnClick = (e) => {
    e.preventDefault();
    if (dropdownOpen) {
      setDropdownOpen(false);
    } else {
      setDropdownOpen(true);
    }
  };

  const DropDownList = () => {
    const elementRef = useRef<HTMLUListElement>(null);
    const handleClickOutside = (event) => {
      if (
        elementRef.current &&
        !elementRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };
    useEffect(() => {
      document.body.addEventListener('click', handleClickOutside);
      return () => {
        document.body.removeEventListener('click', handleClickOutside);
      };
    }, []);
    return (
      <ul
        ref={elementRef}
        className="absolute top-full mt-1 w-full border border-solid border-gray-300 rounded bg-white z-10"
      >
        {options.map((option: DropdownOption, index) => (
          <div
            className={clsx(
              'text-md py-2.5 px-3 border-b border-solid border-gray-300 cursor-pointer last:border-b-0 leading-normal',
              option.value === selectedValue &&
                'font-satoshi-bold pointer-events-none',
              option.disabled
                ? 'pointer-events-none text-gray-400'
                : 'text-gray-900'
            )}
            onClick={() => {
              onOptionSelected(option.value);
              setDropdownOpen(false);
            }}
            key={option.value + index + 'desktop'}
            role={'button'}
            tabIndex={0}
            onKeyPress={(e) => onKeyPressOption(e, option.value)}
          >
            {!isVariantDropdown ? (
              <>{option.label ?? option.value}</>
            ) : (
              <VariantOptionLabel
                stockStatus={option.stockstatus}
                inStock={option.inStock}
                stockDescription={option.stockStatusDescription}
                label={option.label}
              />
            )}
          </div>
        ))}
      </ul>
    );
  };
  return (
    <>
      <div
        className={clsx(
          className,
          isVariantDropdown ? 'hidden' : 'lg:hidden',
          'relative'
        )}
      >
        <select
          value={selectedValue}
          onChange={(e) => onOptionSelected(e.target.value)}
          onClick={() => setDropdownOpen(!dropdownOpen)}
          onBlur={() => setDropdownOpen(false)}
          disabled={disabled}
          className={clsx(
            'dropdown-select',
            'text-lg items-center flex rounded cursor-pointer appearance-none w-full bg-white focus:outline-none',
            size == 'large' ? 'p-4' : 'py-2 px-3'
          )}
        >
          {placeholder && (
            <option disabled value={placeholder} className="text-md">
              {placeholder}
            </option>
          )}
          {options.map((option, index) => (
            <option
              disabled={option.disabled}
              value={option.value}
              key={option.value + index + 'mobile'}
            >
              {option.label ?? option.value}
            </option>
          ))}
        </select>
        <Icon
          name="caret-down"
          className={clsx(
            'absolute right-3 top-1/2 -translate-y-1/2 origin-[50%_0] text-[20px] transition-[rotate] duration-300',
            dropdownOpen && 'rotate-180'
          )}
        />
      </div>
      <div
        className={clsx(
          className,
          !isVariantDropdown && 'hidden lg:block',
          'w-full relative text-gray-900',
          disabled && 'text-gray-100 pointer-events-none'
        )}
        tabIndex={0}
        onKeyPress={onKeyPress}
      >
        <div
          onClick={(e) => handleOnClick(e)}
          className={clsx(
            'dropdown-select',
            'text-lg items-center flex rounded cursor-pointer appearance-none w-full bg-white focus:outline-none leading-normal border border-solid border-gray-600',
            size == 'large' ? 'p-4' : 'py-2 px-3'
          )}
          onKeyPress={onKeyPress}
        >
          <div
            className={clsx(
              'flex-1 max-h-6 overflow-auto',
              !!placeholder && !selectedOption && 'text-gray-400'
            )}
          >
            {selectedOption?.label ?? selectedOption?.value ?? placeholder}
          </div>
          <Icon
            name="caret-down"
            className={clsx(
              'absolute right-3 top-1/2 -translate-y-1/2 text-[20px] transition-transform duration-300',
              dropdownOpen ? 'rotate-180' : 'rotate-0'
            )}
          />
        </div>
        {dropdownOpen && <DropDownList />}
      </div>
    </>
  );
};

export default Dropdown;
